/**
 * NavMenu Component
 * Navigation menu matching Elementor nav-menu styling
 */

'use client'

import { cn } from '@/lib/utils'
import { Menu, X } from 'lucide-react'
import Link from 'next/link'
import React from 'react'

interface NavItem {
	label: string
	href: string
	multiline?: boolean
	external?: boolean
}

interface NavMenuProps {
	items: NavItem[]
	className?: string
}

export function NavMenu({ items, className }: NavMenuProps) {
	const [isOpen, setIsOpen] = React.useState(false)

	return (
		<div className={cn('relative', className)}>
			{/* Desktop Menu */}
			<nav
				className='hidden lg:block'
				aria-label='Menu principal'
			>
				<ul className='flex items-center gap-0.5'>
					{items.map((item, index) => (
						<li key={index}>
							{item.external ? (
								<a
									href={item.href}
									target='_blank'
									rel='noopener noreferrer'
									className={cn(
										'inline-block px-4 py-1.5 text-white font-["Bricolage_Grotesque"] text-[17px] font-bold',
										'hover:text-[#09194D] hover:bg-[#CDFF00] transition-colors',
										'rounded-[40px]',
										item.multiline && 'text-center leading-tight'
									)}
									dangerouslySetInnerHTML={{
										__html: item.label.replace('<br>', '<br />'),
									}}
								/>
							) : (
								<Link
									href={item.href}
									className={cn(
										'inline-block px-4 py-1.5 text-white font-["Bricolage_Grotesque"] text-[17px] font-bold',
										'hover:text-[#09194D] hover:bg-[#CDFF00] transition-colors',
										'rounded-[40px]',
										item.multiline && 'text-center leading-tight'
									)}
									dangerouslySetInnerHTML={{
										__html: item.label.replace('<br>', '<br />'),
									}}
								/>
							)}
						</li>
					))}
				</ul>
			</nav>

			{/* Mobile Menu Toggle */}
			<div className='lg:hidden'>
				<button
					onClick={() => setIsOpen(!isOpen)}
					className='p-2 text-[#CDFF00] cursor-pointer relative z-50'
					aria-label='Alternar menú'
					aria-expanded={isOpen}
				>
					{isOpen ? <X className='w-6 h-6' /> : <Menu className='w-6 h-6' />}
				</button>

				{/* Mobile Overlay Menu */}
				{isOpen && (
					<div className='fixed inset-0 bg-[#09194D] z-40 pt-24 px-6 overflow-y-auto'>
						<nav aria-label='Menu móvil'>
							<ul className='flex flex-col gap-2'>
								{items.map((item, index) => (
									<li
										key={index}
										className='border-b border-[#CDFF00]/20 last:border-0'
									>
										{item.external ? (
											<a
												href={item.href}
												target='_blank'
												rel='noopener noreferrer'
												className='block py-4 text-white font-["Bricolage_Grotesque"] text-xl font-bold hover:text-[#CDFF00] transition-colors'
												onClick={() => setIsOpen(false)}
												dangerouslySetInnerHTML={{
													__html: item.label.replace('<br>', ' '),
												}}
											/>
										) : (
											<Link
												href={item.href}
												className='block py-4 text-white font-["Bricolage_Grotesque"] text-xl font-bold hover:text-[#CDFF00] transition-colors'
												onClick={() => setIsOpen(false)}
												dangerouslySetInnerHTML={{
													__html: item.label.replace('<br>', ' '),
												}}
											/>
										)}
									</li>
								))}
							</ul>
						</nav>
					</div>
				)}
			</div>
		</div>
	)
}
